import { Node } from "@antv/x6";

require('../Graph'); // 注册图形元素

export enum DeviceType {
    Switch = "Switch",
    PC = "PC",
    DHCPServer = "DHCPServer",
    Router = "Router",
    Firewall = "Firewall",
    Modem = "Modem",
    AP = "AP",
    MobileDevice = 'MobileDevice',
    HomeGateway = 'HomeGateway',
    SmartBulb = 'SmartBulb',
    AirConditioner = 'AirConditioner',
    SmartLock = 'SmartLock',
    SmartBell = 'SmartBell',
    SmartSpeaker = 'SmartSpeaker',
    Projector = 'Projector',
    Screen = 'Screen',
    BluetoothMasterDevice = 'BluetoothMasterDevice',
    BluetoothDevice = 'BluetoothDevice',
    InfraredRayDevice = 'InfraredRayDevice',
}

// 广播MAC地址
export const BROARDCAST_MAC = 'FF:FF:FF:FF:FF:FF';

// 硬件抽象层的网络帧数据类型
export enum HALFrameType {
    IPv4 = 0x0800,
    ARP = 0x0806,
    PPPoE = 0x8864,
    IPV6 = 0x86DD,
    InfraredRay = 0x1234,
    Bluetooth = 0x5678,
};

// 硬件抽象层的网络帧结构
export interface HALEthFrame {
    fromMAC: string,
    toMAC: string,
    type: HALFrameType,
    data: any
}

// 硬件抽象层的网络界面
export interface IHALInterface {
    // 判断是否连接上了
    connected: boolean

    // 获取MAC地址
    mac: string

    // 获取对端MAC地址
    connectedMac: string | null

    // 获取对端网卡
    connectedInterface: IHALInterface | null

    // 连线到对端设备
    link(targeDevice: IHALInterface): void

    // 解除连接到对端设备
    unlink(): void

    // 使用当前硬件抽象层界面发送数据
    HAL_Send_Transparent(fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void
    HAL_Send(toMAC: string, type: HALFrameType, data: any, callback: Function): void

    // 其他硬件抽象层界面向本界面发送数据
    HAL_Recv(frame: HALEthFrame, callback: Function): void

    // 转换为JSON
    toJSON(): any

    // 自JSON加载
    fromJSON(config: any): void
}

// 硬件抽象层设备
export interface IHALDevice {
    HAL_Recv(fromInterface: IHALInterface | null, fromMAC: string | null, toMAC: string | null, type: HALFrameType, data: any, callback: Function): void
}

// 仿真设备：主要是实现设备端口的存储与管理
export interface ISimulatorDevice {
    deviceType: DeviceType

    getNode(): Node
    setBridge(bridge: any): void
    getPorts(): Array<IHALInterface>
    destory(): any

    toJSON(): any
    fromJSON(config: any): void
}

// 硬件抽象层界面，可以接收或者发送硬件抽象层帧
export class HALInterface implements IHALInterface {
    // 归属设备
    protected device: IHALDevice

    // 硬件地址
    protected MACAddress: string;

    // 对端设备
    protected linkTo: IHALInterface | null;

    // 对端设备MAC地址
    protected linkToMac: string | null;

    constructor(device: IHALDevice, MACAddress: string) {
        // 归属设备
        this.device = device;

        // 硬件地址
        this.MACAddress = MACAddress;

        // 默认不建立连接
        this.linkTo = null;
        this.linkToMac = null;
    }

    // 连线到对端设备
    link(targetInterface: IHALInterface) {
        if (this.linkTo) {
            this.linkTo.unlink();
        }

        // 更新本地连接到对端
        this.linkTo = targetInterface;
        this.linkToMac = targetInterface.mac;
    }

    // 解除连接到对端设备
    unlink() {
        this.linkTo = null;
        this.linkToMac = null;
    }

    // 获取对端网卡
    get connectedInterface(): IHALInterface | null {
        return this.linkTo;
    }

    // 判断是否连接上了
    get connected() {
        return this.linkTo !== null;
    }

    // 获取MAC地址
    get mac() {
        return this.MACAddress;
    }

    // 获取对端MAC地址
    get connectedMac() {
        return this.linkToMac;
    }

    // 使用当前硬件抽象层界面发送硬件抽象层数据
    HAL_Send_Transparent(fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 检测对端是否存在，不存在直接报错
        const { linkTo } = this;
        if (!linkTo) {
            throw new Error('对端硬件抽象层设备不存在!');
        }

        // 构造硬件抽象层帧
        const frame: HALEthFrame = {
            fromMAC,
            toMAC,
            type,
            data
        }

        // 调用对端节点的HAL_Recv
        linkTo.HAL_Recv(frame, callback);
    }

    // 使用当前硬件抽象层界面发送硬件抽象层数据
    HAL_Send(toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        this.HAL_Send_Transparent(this.MACAddress, toMAC, type, data, callback);
    }

    // 其他硬件抽象层界面向本界面发送硬件抽象层数据
    HAL_Recv(frame: HALEthFrame, callback: Function): void {
        // 构造硬件抽象层帧
        const {
            fromMAC,
            toMAC,
            type,
            data,
        } = frame;

        // 自身发送报文丢弃
        if (fromMAC === this.MACAddress) {
            return;
        }

        // 非广播报文且非本端口报文丢弃
        if ((toMAC !== BROARDCAST_MAC) && (toMAC !== this.MACAddress)) {
            return;
        }

        this.device.HAL_Recv(this, fromMAC, toMAC, type, data, callback);
    }

    // 处理其他硬件抽象层界面发送到的硬件抽象层数据
    HAL_ParseRecvPacket(fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        throw new Error('未实现');
    }

    // 转换为JSON
    toJSON(): any {
        return {
            mac: this.MACAddress,
            linkToMac: this.linkToMac,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        const { mac, linkToMac } = config;
        this.MACAddress = mac;
        this.linkToMac = linkToMac;
    }
}
